import { forwardRef, useState, ReactNode } from 'react';
import {
  Box,
  Group,
  Stack,
  Text,
  Badge,
  Avatar,
  Paper,
  ActionIcon,
  Menu,
  Progress,
  Tooltip,
  ScrollArea,
} from '@mantine/core';
import {
  IconDots,
  IconEdit,
  IconTrash,
  IconCurrencyDollar,
  IconCalendar,
  IconUser,
  IconPlus,
  IconGripVertical,
} from '@tabler/icons-react';
import { Card, Button } from '../../ui';
import { FadeIn, HoverLift } from '../../animations';
import { usePreferencesStore } from '../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../hooks/useAccessibilityStyles';

export interface Deal {
  id: string;
  title: string;
  value: number;
  currency?: string;
  stage: string;
  probability?: number;
  expectedCloseDate?: Date;
  contact?: {
    id: string;
    name: string;
    avatar?: string;
    company?: string;
  };
  owner?: {
    id: string;
    name: string;
    avatar?: string;
  };
  tags?: string[];
  priority?: 'low' | 'medium' | 'high';
  lastActivity?: Date;
  metadata?: Record<string, unknown>;
}

export interface PipelineStage {
  key: string;
  label: string;
  color?: string;
  probability?: number;
}

export interface DealPipelineProps {
  stages: PipelineStage[];
  deals: Deal[];
  onDealMove?: (dealId: string, fromStage: string, toStage: string) => void;
  onDealClick?: (deal: Deal) => void;
  onDealEdit?: (deal: Deal) => void;
  onDealDelete?: (deal: Deal) => void;
  onAddDeal?: (stage: string) => void;
  renderDeal?: (deal: Deal) => ReactNode;
  currency?: string;
  showTotalValue?: boolean;
  showProbability?: boolean;
  className?: string;
}

export const DealPipeline = forwardRef<HTMLDivElement, DealPipelineProps>(
  (
    {
      stages,
      deals,
      onDealMove,
      onDealClick,
      onDealEdit,
      onDealDelete,
      onAddDeal,
      renderDeal,
      currency = 'USD',
      showTotalValue = true,
      showProbability = true,
      className,
    },
    ref
  ) => {
    const [draggedDeal, setDraggedDeal] = useState<string | null>(null);
    const [dragOverStage, setDragOverStage] = useState<string | null>(null);
    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    const formatCurrency = (amount: number, cur = currency) => {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: cur,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(amount);
    };

    const formatDate = (date?: Date) => {
      if (!date) return '';
      return new Date(date).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
      });
    };

    const getDealsForStage = (stageKey: string) =>
      deals.filter((deal) => deal.stage === stageKey);

    const getStageTotal = (stageKey: string) =>
      getDealsForStage(stageKey).reduce((sum, deal) => sum + deal.value, 0);

    const getWeightedValue = (stageKey: string) => {
      const stage = stages.find((s) => s.key === stageKey);
      const stageDeals = getDealsForStage(stageKey);
      return stageDeals.reduce((sum, deal) => {
        const prob = deal.probability ?? stage?.probability ?? 100;
        return sum + deal.value * (prob / 100);
      }, 0);
    };

    const getPriorityColor = (priority?: string) => {
      switch (priority) {
        case 'high':
          return 'red';
        case 'medium':
          return 'orange';
        case 'low':
          return 'green';
        default:
          return 'gray';
      }
    };

    const handleDragStart = (e: React.DragEvent, dealId: string) => {
      setDraggedDeal(dealId);
      e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragEnd = () => {
      setDraggedDeal(null);
      setDragOverStage(null);
    };

    const handleDragOver = (e: React.DragEvent, stageKey: string) => {
      e.preventDefault();
      setDragOverStage(stageKey);
    };

    const handleDrop = (e: React.DragEvent, toStage: string) => {
      e.preventDefault();
      if (!draggedDeal) return;

      const deal = deals.find((d) => d.id === draggedDeal);
      if (deal && deal.stage !== toStage) {
        onDealMove?.(draggedDeal, deal.stage, toStage);
      }

      setDraggedDeal(null);
      setDragOverStage(null);
    };

    const renderDefaultDeal = (deal: Deal) => {
      const stage = stages.find((s) => s.key === deal.stage);

      return (
        <HoverLift lift={4}>
          <Paper
            p="sm"
            withBorder
            draggable
            onDragStart={(e) => handleDragStart(e, deal.id)}
            onDragEnd={handleDragEnd}
            style={{
              cursor: 'grab',
              opacity: draggedDeal === deal.id ? 0.5 : 1,
            }}
            onClick={() => onDealClick?.(deal)}
          >
            <Stack gap="xs">
              {/* Header */}
              <Group justify="space-between" wrap="nowrap">
                <Group gap={4}>
                  <IconGripVertical size={14} color="gray" />
                  {deal.priority && (
                    <Tooltip label={`${deal.priority} priority`}>
                      <Box
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: `var(--mantine-color-${getPriorityColor(deal.priority)}-5)`,
                        }}
                      />
                    </Tooltip>
                  )}
                </Group>
                {(onDealEdit || onDealDelete) && (
                  <Menu position="bottom-end" withinPortal>
                    <Menu.Target>
                      <ActionIcon
                        size="xs"
                        variant="subtle"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <IconDots size={14} />
                      </ActionIcon>
                    </Menu.Target>
                    <Menu.Dropdown>
                      {onDealEdit && (
                        <Menu.Item
                          leftSection={<IconEdit size={14} />}
                          onClick={(e) => {
                            e.stopPropagation();
                            onDealEdit(deal);
                          }}
                        >
                          Edit
                        </Menu.Item>
                      )}
                      {onDealDelete && (
                        <Menu.Item
                          leftSection={<IconTrash size={14} />}
                          color="red"
                          onClick={(e) => {
                            e.stopPropagation();
                            onDealDelete(deal);
                          }}
                        >
                          Delete
                        </Menu.Item>
                      )}
                    </Menu.Dropdown>
                  </Menu>
                )}
              </Group>

              {/* Title */}
              <Text size="sm" fw={500} lineClamp={2} style={accessibilityStyles}>
                {deal.title}
              </Text>

              {/* Value */}
              <Group gap="xs">
                <IconCurrencyDollar size={14} color="green" />
                <Text size="sm" fw={600} c="green" style={accessibilityStyles}>
                  {formatCurrency(deal.value, deal.currency)}
                </Text>
              </Group>

              {/* Contact */}
              {deal.contact && (
                <Group gap="xs">
                  <Avatar src={deal.contact.avatar} size="xs" radius="xl">
                    {deal.contact.name.charAt(0)}
                  </Avatar>
                  <Box style={{ overflow: 'hidden' }}>
                    <Text size="xs" lineClamp={1} style={accessibilityStyles}>{deal.contact.name}</Text>
                    {deal.contact.company && (
                      <Text size="xs" c="dimmed" lineClamp={1} style={accessibilityStyles}>
                        {deal.contact.company}
                      </Text>
                    )}
                  </Box>
                </Group>
              )}

              {/* Probability */}
              {showProbability && deal.probability !== undefined && (
                <Box>
                  <Group justify="space-between" mb={2}>
                    <Text size="xs" c="dimmed" style={accessibilityStyles}>Win probability</Text>
                    <Text size="xs" fw={500} style={accessibilityStyles}>{deal.probability}%</Text>
                  </Group>
                  <Progress value={deal.probability} size="xs" color={stage?.color} />
                </Box>
              )}

              {/* Footer */}
              <Group justify="space-between">
                {deal.expectedCloseDate && (
                  <Group gap={2}>
                    <IconCalendar size={12} color="gray" />
                    <Text size="xs" c="dimmed" style={accessibilityStyles}>{formatDate(deal.expectedCloseDate)}</Text>
                  </Group>
                )}
                {deal.owner && (
                  <Tooltip label={deal.owner.name}>
                    <Avatar src={deal.owner.avatar} size="xs" radius="xl">
                      {deal.owner.name.charAt(0)}
                    </Avatar>
                  </Tooltip>
                )}
              </Group>

              {/* Tags */}
              {deal.tags && deal.tags.length > 0 && (
                <Group gap={4}>
                  {deal.tags.slice(0, 2).map((tag) => (
                    <Badge key={tag} size="xs" variant="light">
                      {tag}
                    </Badge>
                  ))}
                  {deal.tags.length > 2 && (
                    <Text size="xs" c="dimmed" style={accessibilityStyles}>+{deal.tags.length - 2}</Text>
                  )}
                </Group>
              )}
            </Stack>
          </Paper>
        </HoverLift>
      );
    };

    const totalPipelineValue = deals.reduce((sum, deal) => sum + deal.value, 0);
    const totalWeightedValue = stages.reduce((sum, stage) => sum + getWeightedValue(stage.key), 0);

    return (
      <FadeIn>
        <Box ref={ref} className={className}>
          {/* Summary */}
          {showTotalValue && (
            <Paper p="md" mb="md" bg="gray.0" radius="md">
              <Group justify="space-around">
                <Box ta="center">
                  <Text size="xs" c="dimmed" style={accessibilityStyles}>Total Pipeline Value</Text>
                  <Text size="xl" fw={700} style={accessibilityStyles}>{formatCurrency(totalPipelineValue)}</Text>
                </Box>
                <Box ta="center">
                  <Text size="xs" c="dimmed" style={accessibilityStyles}>Weighted Value</Text>
                  <Text size="xl" fw={700} c="blue" style={accessibilityStyles}>{formatCurrency(totalWeightedValue)}</Text>
                </Box>
                <Box ta="center">
                  <Text size="xs" c="dimmed" style={accessibilityStyles}>Total Deals</Text>
                  <Text size="xl" fw={700} style={accessibilityStyles}>{deals.length}</Text>
                </Box>
              </Group>
            </Paper>
          )}

          {/* Pipeline columns */}
          <ScrollArea>
            <Group gap="md" align="flex-start" wrap="nowrap" style={{ minWidth: 'fit-content' }}>
              {stages.map((stage) => {
                const stageDeals = getDealsForStage(stage.key);
                const stageTotal = getStageTotal(stage.key);
                const isDragOver = dragOverStage === stage.key;

                return (
                  <Card
                    key={stage.key}
                    p="sm"
                    style={{
                      width: 300,
                      minWidth: 300,
                      backgroundColor: isDragOver
                        ? 'var(--mantine-color-blue-0)'
                        : undefined,
                      borderColor: isDragOver
                        ? 'var(--mantine-color-blue-5)'
                        : undefined,
                    }}
                    onDragOver={(e) => handleDragOver(e, stage.key)}
                    onDrop={(e) => handleDrop(e, stage.key)}
                  >
                    <Stack gap="sm">
                      {/* Stage header */}
                      <Group justify="space-between">
                        <Group gap="xs">
                          <Box
                            style={{
                              width: 12,
                              height: 12,
                              borderRadius: 3,
                              backgroundColor: `var(--mantine-color-${stage.color || 'blue'}-5)`,
                            }}
                          />
                          <Text fw={600} style={accessibilityStyles}>{stage.label}</Text>
                          <Badge size="sm" variant="light">
                            {stageDeals.length}
                          </Badge>
                        </Group>
                        {onAddDeal && (
                          <ActionIcon
                            size="sm"
                            variant="subtle"
                            onClick={() => onAddDeal(stage.key)}
                          >
                            <IconPlus size={16} />
                          </ActionIcon>
                        )}
                      </Group>

                      {/* Stage value */}
                      <Text size="sm" c="dimmed" style={accessibilityStyles}>
                        {formatCurrency(stageTotal)}
                        {showProbability && stage.probability && (
                          <Text span c="dimmed" size="xs"> ({stage.probability}%)</Text>
                        )}
                      </Text>

                      {/* Deals */}
                      <ScrollArea.Autosize mah={500}>
                        <Stack gap="xs">
                          {stageDeals.map((deal) => (
                            <Box key={deal.id}>
                              {renderDeal ? renderDeal(deal) : renderDefaultDeal(deal)}
                            </Box>
                          ))}
                          {stageDeals.length === 0 && (
                            <Paper
                              p="md"
                              style={{
                                border: '2px dashed var(--mantine-color-gray-3)',
                                backgroundColor: 'transparent',
                              }}
                            >
                              <Text size="sm" c="dimmed" ta="center" style={accessibilityStyles}>
                                No deals
                              </Text>
                            </Paper>
                          )}
                        </Stack>
                      </ScrollArea.Autosize>
                    </Stack>
                  </Card>
                );
              })}
            </Group>
          </ScrollArea>
        </Box>
      </FadeIn>
    );
  }
);

DealPipeline.displayName = 'DealPipeline';

export default DealPipeline;
